import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Switch,
  CircularProgress,
} from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const PRESET_COLORS = [
  "#2563eb", "#1e3a8a", "#0891b2", "#0f766e",
  "#16a34a", "#ca8a04", "#ea580c", "#dc2626",
  "#db2777", "#7c3aed", "#4f46e5", "#0f172a",
];
const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const darken = (hex, amount = 0.3) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const n = parseInt(h, 16);
  const f = (v) => Math.round(v * (1 - amount));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => f(v).toString(16).padStart(2, "0")).join("")}`;
};

// Cor do texto sobre a cor da marca (mesmo cálculo da página pública).
export const readableOn = (hex) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "#fff";
  const lin = (v) => {
    const c = parseInt(v, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * lin(h.slice(0, 2)) + 0.7152 * lin(h.slice(2, 4)) + 0.0722 * lin(h.slice(4, 6));
  return L > 0.55 ? "#1f2937" : "#fff";
};

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();

const formatPrice = (v) =>
  Number(v || 0) > 0 ? Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "Grátis";

const formatDuration = (minutes) => {
  const m = Number(minutes) || 0;
  if (m < 60) return `${m} min`;
  const rest = m % 60;
  return rest ? `${Math.floor(m / 60)}h${String(rest).padStart(2, "0")}` : `${m / 60} h`;
};

const useStyles = makeStyles((theme) => ({
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 380px",
    gap: theme.spacing(2),
    alignItems: "start",
    color: theme.palette.text.primary,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  card: { borderRadius: 14, padding: theme.spacing(0.5, 2.5) },
  section: {
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  hint: { fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.45 },
  ok: { color: theme.palette.type === "dark" ? "#81c784" : "#2e7d32" },
  bad: { color: theme.palette.error.main },
  row: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap", marginTop: theme.spacing(1.25) },
  smallButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
  },
  warn: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    marginTop: theme.spacing(1.25),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.45,
    backgroundColor: alpha(theme.palette.warning.main, theme.palette.type === "dark" ? 0.16 : 0.12),
    "& svg": { color: theme.palette.warning.main, flexShrink: 0 },
  },
  swatches: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: theme.spacing(1.25) },
  swatch: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "1px solid rgba(0,0,0,.12)",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    font: "700 13px inherit",
    padding: 0,
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  swatchOn: { boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.text.primary}` },
  logoRow: { display: "flex", alignItems: "center", gap: theme.spacing(1.75), flexWrap: "wrap" },
  logoBox: {
    width: 72,
    height: 72,
    borderRadius: 14,
    border: `1px dashed ${theme.palette.divider}`,
    display: "grid",
    placeItems: "center",
    fontSize: 12,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    overflow: "hidden",
  },
  logoImg: { width: "100%", height: "100%", objectFit: "contain", background: "#fff", padding: 4 },
  switchRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    "& + &": { marginTop: theme.spacing(1.25) },
  },
  saveBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    padding: theme.spacing(1.75, 0),
  },
  saveButton: { textTransform: "none", fontWeight: 600, borderRadius: 10, height: 40, padding: theme.spacing(0, 2.5) },
  previewLabel: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.palette.text.secondary,
    margin: theme.spacing(0, 0, 1, 0.5),
  },
  pCard: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  pHero: { padding: theme.spacing(2.25, 2.5, 2.5) },
  pBrand: { display: "flex", alignItems: "center", gap: 14 },
  pLogo: {
    width: 52,
    height: 52,
    borderRadius: 14,
    background: "#fff",
    display: "grid",
    placeItems: "center",
    fontWeight: 800,
    fontSize: 18,
    flexShrink: 0,
    overflow: "hidden",
    boxShadow: "0 4px 14px rgba(0,0,0,.18)",
  },
  pBody: { padding: theme.spacing(2, 2.5, 2.5), display: "grid", gap: 8 },
  pService: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    border: `1.5px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(1.5, 1.75),
  },
  pButton: {
    marginTop: 6,
    borderRadius: 10,
    padding: "10px 0",
    textAlign: "center",
    fontWeight: 700,
    fontSize: 14,
  },
}));

const BookingPageSettings = ({ onSlugSaved }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const companyId = user.companyId;

  const [loaded, setLoaded] = useState(false);
  const [saved, setSaved] = useState(null);
  const [slug, setSlug] = useState("");
  const [slugCheck, setSlugCheck] = useState(null);
  const [brandColors, setBrandColors] = useState([]);
  const [customColor, setCustomColor] = useState("");
  const [showPrices, setShowPrices] = useState(true);
  const [chooseProvider, setChooseProvider] = useState(true);
  const [logo, setLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewServices, setPreviewServices] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const logoInputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/slug`);
        const state = {
          slug: data.slug || "",
          brandColors: data.brandColors || [],
          showPrices: data.bookingShowPrices !== false,
          chooseProvider: data.bookingChooseProvider !== false,
        };
        setSaved(state);
        setSlug(state.slug);
        setBrandColors(state.brandColors);
        setShowPrices(state.showPrices);
        setChooseProvider(state.chooseProvider);
        setLogo(data.logo || null);
      } catch (err) {
        toastError(err);
      }
      setLoaded(true);
    };
    const loadPreview = async () => {
      try {
        const { data } = await api.get("/services", { params: { pageNumber: 1, activeOnly: "true" } });
        setPreviewServices(
          (data.services || []).filter((s) => (s.users || []).some((u) => u.isServiceProvider)).slice(0, 3)
        );
      } catch (err) {
        // prévia sem serviços
      }
      try {
        const { data } = await api.get(`/companies/${companyId}`);
        setCompanyName(data.name || "");
      } catch (err) {
        // usa nome genérico na prévia
      }
    };
    load();
    loadPreview();
  }, [companyId]);

  // Confere se o link está livre enquanto o admin digita.
  useEffect(() => {
    if (!saved || slug === saved.slug) {
      setSlugCheck(null);
      return undefined;
    }
    if (!SLUG_RE.test(slug) || slug.length < 3 || slug.length > 40) {
      setSlugCheck({ valid: false });
      return undefined;
    }
    setSlugCheck({ checking: true });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/slug-check`, { params: { slug } });
        setSlugCheck(data);
      } catch (err) {
        setSlugCheck(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [slug, saved, companyId]);

  const slugChanged = saved && slug !== saved.slug;
  const colorsChanged = saved && brandColors.join(",") !== saved.brandColors.join(",");
  const optionsChanged = saved && (showPrices !== saved.showPrices || chooseProvider !== saved.chooseProvider);
  const dirty = slugChanged || colorsChanged || optionsChanged;
  const slugBlocked = slugChanged && (!slugCheck || slugCheck.checking || !slugCheck.valid || !slugCheck.available);

  const dirtyLabel = [slugChanged && "link", colorsChanged && "cores", optionsChanged && "opções"]
    .filter(Boolean)
    .join(", ");

  const publicUrl = saved && saved.slug ? `${window.location.origin}/agenda/${saved.slug}` : "";

  const handleSlugInput = (value) =>
    setSlug(
      value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9-]/g, "-")
        .replace(/-{2,}/g, "-")
        .slice(0, 40)
    );

  const toggleColor = (c) =>
    setBrandColors((prev) => {
      if (prev.includes(c)) return prev.filter((x) => x !== c);
      if (prev.length >= 3) return [...prev.slice(1), c];
      return [...prev, c];
    });

  const addCustomColor = () => {
    const c = customColor.trim().toLowerCase();
    if (!HEX_RE.test(c)) {
      toast.error("Cor inválida. Use o formato #RRGGBB.");
      return;
    }
    if (!brandColors.includes(c)) toggleColor(c);
    setCustomColor("");
  };

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copiado!");
    } catch (err) {
      toast.info(publicUrl);
    }
  };

  const handleSave = async () => {
    if (slugBlocked) return;
    setSaving(true);
    try {
      const next = { ...saved };
      if (slugChanged) {
        const { data } = await api.put(`/companies/${companyId}/slug`, { slug });
        next.slug = data.slug;
        setSlug(data.slug);
        if (onSlugSaved) onSlugSaved(data.slug);
      }
      if (colorsChanged) {
        const { data } = await api.put(`/companies/${companyId}/brand-colors`, { brandColors });
        next.brandColors = data.brandColors || [];
        setBrandColors(next.brandColors);
      }
      if (optionsChanged) {
        const { data } = await api.put(`/companies/${companyId}/booking-settings`, {
          bookingShowPrices: showPrices,
          bookingChooseProvider: chooseProvider,
        });
        next.showPrices = data.bookingShowPrices;
        next.chooseProvider = data.bookingChooseProvider;
      }
      setSaved(next);
      toast.success("Página pública salva.");
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const handleLogoSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    const inputEl = e.target; // React 16 anula e.target depois do await
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("Formato inválido. Use PNG, JPG ou WEBP.");
      inputEl.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem muito grande. Máximo de 2 MB.");
      inputEl.value = "";
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setUploadingLogo(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/logo`, formData);
      setLogo(data.logo);
      toast.success("Logo enviada.");
    } catch (err) {
      toastError(err);
    }
    setUploadingLogo(false);
    inputEl.value = "";
  };

  const handleRemoveLogo = async () => {
    setUploadingLogo(true);
    try {
      await api.delete(`/companies/${companyId}/logo`);
      setLogo(null);
      toast.success("Logo removida.");
    } catch (err) {
      toastError(err);
    }
    setUploadingLogo(false);
  };

  if (!loaded) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <CircularProgress size={24} />
      </div>
    );
  }

  const logoUrl = logo ? `${process.env.REACT_APP_BACKEND_URL}/public/${logo}` : "";
  const primary = brandColors[0] || "#2563eb";
  const gradientEnd = brandColors[1] || darken(primary, 0.3);
  const onPrimary = readableOn(primary);

  const slugHint = () => {
    if (!slugChanged) {
      return <span className={classes.hint}>Só letras minúsculas, números e hífen (3 a 40).</span>;
    }
    if (!slugCheck || slugCheck.checking) return <span className={classes.hint}>Conferindo…</span>;
    if (!slugCheck.valid) {
      return <span className={classes.bad}>Use de 3 a 40 caracteres: letras minúsculas, números e hífen.</span>;
    }
    if (!slugCheck.available) return <span className={classes.bad}>Este link já está em uso por outra empresa.</span>;
    return <span className={classes.ok}>✓ Disponível</span>;
  };

  return (
    <div className={classes.layout}>
      <Paper variant="outlined" className={classes.card}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Endereço do link</div>
          <TextField
            id="booking-slug"
            value={slug}
            onChange={(e) => handleSlugInput(e.target.value)}
            placeholder="seu-negocio"
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ maxLength: 40, "aria-label": "Endereço do link" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{window.location.host}/agenda/</InputAdornment>
              ),
            }}
          />
          <div style={{ marginTop: 6, fontSize: 12 }}>{slugHint()}</div>
          {publicUrl && (
            <div className={classes.row}>
              <Button variant="outlined" size="small" className={classes.smallButton} onClick={handleCopy}>
                Copiar link
              </Button>
              <Button
                variant="outlined"
                size="small"
                className={classes.smallButton}
                endIcon={<OpenInNewIcon style={{ fontSize: 16 }} />}
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir página
              </Button>
            </div>
          )}
          {saved.slug && slugChanged && (
            <div className={classes.warn}>
              <WarningRoundedIcon fontSize="small" />
              <span>Trocar o endereço quebra o link antigo que você já divulgou (Instagram, Google, cartões).</span>
            </div>
          )}
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>Cores da marca</div>
          <span className={classes.hint}>
            Até 3. A 1ª vai nos botões; a 1ª e a 2ª formam o fundo do topo. Sem cor, usamos o azul padrão.
          </span>
          <div className={classes.swatches}>
            {PRESET_COLORS.concat(brandColors.filter((c) => !PRESET_COLORS.includes(c))).map((c) => {
              const i = brandColors.indexOf(c);
              return (
                <button
                  key={c}
                  type="button"
                  title={c}
                  aria-label={`Cor ${c}`}
                  aria-pressed={i > -1}
                  className={`${classes.swatch} ${i > -1 ? classes.swatchOn : ""}`}
                  style={{ backgroundColor: c, color: readableOn(c) }}
                  onClick={() => toggleColor(c)}
                >
                  {i > -1 ? i + 1 : ""}
                </button>
              );
            })}
          </div>
          <div className={classes.row}>
            <TextField
              id="booking-custom-color"
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              placeholder="#FF5733"
              size="small"
              variant="outlined"
              style={{ width: 130 }}
              inputProps={{ "aria-label": "Cor personalizada" }}
            />
            <Button size="small" className={classes.smallButton} onClick={addCustomColor}>
              Adicionar cor
            </Button>
          </div>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>Logo</div>
          <div className={classes.logoRow}>
            <div className={classes.logoBox}>
              {logoUrl ? <img src={logoUrl} alt="Logo" className={classes.logoImg} /> : "Sem logo"}
            </div>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: "none" }}
              onChange={handleLogoSelected}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.smallButton}
                  disabled={uploadingLogo}
                  onClick={() => logoInputRef.current && logoInputRef.current.click()}
                >
                  {uploadingLogo ? "Enviando…" : logoUrl ? "Trocar logo" : "Enviar logo"}
                </Button>
                {logoUrl && (
                  <Button size="small" className={classes.smallButton} style={{ color: "#dc2626" }} disabled={uploadingLogo} onClick={handleRemoveLogo}>
                    Remover
                  </Button>
                )}
              </div>
              <span className={classes.hint}>PNG, JPG ou WEBP, até 2 MB. Sem logo, usamos as iniciais. Salva na hora.</span>
            </div>
          </div>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>Informações exibidas</div>
          <div className={classes.switchRow}>
            <div>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                Mostrar preços
              </Typography>
              <span className={classes.hint}>Desligado: o cliente vê só o nome e a duração.</span>
            </div>
            <Switch
              color="primary"
              checked={showPrices}
              onChange={(e) => setShowPrices(e.target.checked)}
              inputProps={{ "aria-label": "Mostrar preços" }}
            />
          </div>
          <div className={classes.switchRow}>
            <div>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                Cliente escolhe o profissional
              </Typography>
              <span className={classes.hint}>Desligado: mostra só os horários e o sistema escolhe quem atende.</span>
            </div>
            <Switch
              color="primary"
              checked={chooseProvider}
              onChange={(e) => setChooseProvider(e.target.checked)}
              inputProps={{ "aria-label": "Cliente escolhe o profissional" }}
            />
          </div>
        </div>

        <div className={classes.saveBar}>
          <span className={classes.hint}>{dirty ? `Alterações não salvas: ${dirtyLabel}` : "Tudo salvo"}</span>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.saveButton}
            disabled={!dirty || saving || slugBlocked}
            onClick={handleSave}
          >
            {saving ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      </Paper>

      <div>
        <div className={classes.previewLabel}>Prévia</div>
        <div className={classes.pCard}>
          <div className={classes.pHero} style={{ background: `linear-gradient(135deg, ${primary}, ${gradientEnd})`, color: onPrimary }}>
            <div className={classes.pBrand}>
              <div className={classes.pLogo} style={{ color: primary }}>
                {logoUrl ? (
                  <img src={logoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 6 }} />
                ) : (
                  initials(companyName || "Sua empresa")
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1.15, color: onPrimary }}>
                  {companyName || "Sua empresa"}
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.85, color: onPrimary }}>
                  Agende seu horário online
                </Typography>
              </div>
            </div>
          </div>
          <div className={classes.pBody}>
            {previewServices.length === 0 ? (
              <span className={classes.hint}>
                Nenhum serviço aparece ainda. Cadastre um serviço ativo com ao menos um profissional.
              </span>
            ) : (
              previewServices.map((s, i) => (
                <div
                  key={s.id}
                  className={classes.pService}
                  style={i === 0 ? { borderColor: primary, boxShadow: `0 0 0 1px ${primary}` } : undefined}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{s.name}</div>
                    <span className={classes.hint}>{formatDuration(s.durationMinutes)}</span>
                  </div>
                  {showPrices && <strong style={{ fontVariantNumeric: "tabular-nums" }}>{formatPrice(s.price)}</strong>}
                </div>
              ))
            )}
            <div className={classes.pButton} style={{ backgroundColor: primary, color: onPrimary }}>
              Continuar
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPageSettings;
